import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Save, X, Upload, Eye, EyeOff, ArrowUp, ArrowDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Predefined area options
const PREDEFINED_AREAS = [
  { slug: 'mental-health', title: 'Mental Health', icon: '🧠' },
  { slug: 'sports-medicine', title: 'Sports Medicine', icon: '⚡' },
  { slug: 'reproductive-health', title: 'Reproductive Health', icon: '🌸' },
  { slug: 'pain-management', title: 'Pain Management', icon: '💪' },
  { slug: 'general-care', title: 'General Care', icon: '🏥' },
  { slug: 'nutrition-guidance', title: 'Nutrition Guidance', icon: '🥗' }
];

export default function AreasManager() {
  const navigate = useNavigate();
  const [areas, setAreas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [useCustomSlug, setUseCustomSlug] = useState(false);
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    subtitle: '',
    description: '',
    additional_details: '',
    order: 0,
    enabled: true,
    image_1_link: '',  // New field for image 1 link
    image_2_link: ''   // New field for image 2 link
  });

  useEffect(() => {
    loadAreas();
  }, []);

  const loadAreas = async () => {
    try {
      const response = await axios.get(`${API}/admin/areas`, {
        withCredentials: true
      });
      setAreas(response.data);
    } catch (error) {
      toast.error('Failed to load areas');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate slug is not empty
    if (!formData.slug || formData.slug.trim() === '') {
      toast.error('Please select an area or create a custom area with a valid slug');
      return;
    }
    
    try {
      const submitData = {
        ...formData
      };
      
      if (editingArea) {
        await axios.put(`${API}/admin/areas/${editingArea.id}`, submitData, {
          withCredentials: true
        });
        toast.success('Area updated!');
      } else {
        await axios.post(`${API}/admin/areas`, submitData, {
          withCredentials: true
        });
        toast.success('Area created!');
      }
      
      setShowForm(false);
      setEditingArea(null);
      resetForm();
      loadAreas();
    } catch (error) {
      // Better error handling for Pydantic validation errors
      let errorMessage = 'Failed to save area';
      if (error.response?.data) {
        const data = error.response.data;
        if (data.detail) {
          errorMessage = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
        } else if (Array.isArray(data)) {
          errorMessage = data.map(e => `${e.loc?.join('.') || 'field'}: ${e.msg}`).join(', ');
        }
      }
      toast.error(errorMessage);
      console.error('Save area error:', error.response?.data);
    }
  };

  const handleEdit = (area) => {
    // Navigate to edit page instead of showing form
    navigate(`/admin/areas/edit/${area.id}`);
  };

  const handleDelete = async (areaId) => {
    if (!window.confirm('Delete this area? This will remove it from the website.')) return;
    
    try {
      await axios.delete(`${API}/admin/areas/${areaId}`, {
        withCredentials: true
      });
      toast.success('Area deleted');
      loadAreas();
    } catch (error) {
      toast.error('Failed to delete area');
    }
  };

  const handleToggleEnabled = async (area) => {
    try {
      const updated = { ...area, enabled: !area.enabled };
      await axios.put(`${API}/admin/areas/${area.id}`, updated, {
        withCredentials: true
      });
      toast.success(updated.enabled ? 'Area enabled' : 'Area disabled');
      loadAreas();
    } catch (error) {
      toast.error('Failed to update area');
    }
  };

  const resetForm = () => {
    setFormData({
      slug: '',
      title: '',
      subtitle: '',
      description: '',
      additional_details: '',
      order: 0,
      enabled: true,
      image_1_link: '',
      image_2_link: ''
    });
    setUseCustomSlug(false);
  };

  const handlePredefinedSelect = (slug) => {
    const predefined = PREDEFINED_AREAS.find(p => p.slug === slug);
    if (predefined) {
      setFormData({
        ...formData,
        slug: predefined.slug,
        title: predefined.title
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl retro-title text-[#2C1810]">Areas of Focus</h2>
          <p className="text-[#6B4423] mt-1">Manage the specialty areas shown on your homepage</p>
        </div>
        <button
          onClick={() => navigate('/admin/areas/new')}
          className="retro-button-small flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Area
        </button>
      </div>

      {/* Areas List */}
      <div className="space-y-4">
        {areas.length === 0 ? (
          <div className="cassette-card p-12 text-center">
            <p className="text-[#6B4423] mb-4">No areas created yet. Add your first area to get started!</p>
            <button
              onClick={() => navigate('/admin/areas/new')}
              className="retro-button inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Create First Area
            </button>
          </div>
        ) : (
          areas.map((area) => (
            <div key={area.id} className="cassette-card p-6">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-2xl retro-title text-[#2C1810]">{area.title}</h3>
                    {!area.enabled && (
                      <span className="text-xs px-2 py-1 bg-gray-400 text-white rounded">Hidden</span>
                    )}
                    <span className="text-xs px-2 py-1 bg-[#E8934D] text-white rounded">Order: {area.order}</span>
                  </div>
                  {area.subtitle && (
                    <p className="text-[#6B4423] mb-2 font-semibold">{area.subtitle}</p>
                  )}
                  <p className="text-sm text-[#6B4423] mb-4 line-clamp-2">{area.description}</p>
                </div>
                
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => handleToggleEnabled(area)}
                    className={`retro-button-small ${area.enabled ? 'bg-[#4D9E7C]' : 'bg-gray-400'}`}
                    title={area.enabled ? 'Hide from website' : 'Show on website'}
                  >
                    {area.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => handleEdit(area)}
                    className="retro-button-small bg-[#E8934D]"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(area.id)}
                    className="retro-button-small bg-[#E85D4D]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="cassette-card max-w-3xl w-full p-8 my-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl retro-title text-[#2C1810]">
                {editingArea ? 'Edit Area' : 'Add New Area'}
              </h3>
              <button onClick={() => setShowForm(false)} className="text-[#6B4423] hover:text-[#E85D4D]">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Area Selection */}
              {!editingArea && (
                <div className="bg-[#E8934D]/10 border-2 border-[#E8934D] rounded p-4">
                  <label className="block text-sm font-semibold text-[#2C1810] mb-3">
                    Select Area Type *
                  </label>
                  <div className="space-y-3">
                    {/* Predefined Areas Dropdown */}
                    <div>
                      <label className="block text-xs text-[#6B4423] mb-2">Choose from predefined areas:</label>
                      <select
                        value={useCustomSlug ? 'custom' : formData.slug}
                        onChange={(e) => {
                          if (e.target.value === 'custom') {
                            setUseCustomSlug(true);
                            setFormData({ ...formData, slug: '', title: '' });
                          } else {
                            setUseCustomSlug(false);
                            handlePredefinedSelect(e.target.value);
                          }
                        }}
                        className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                      >
                        <option value="">-- Select an area --</option>
                        {PREDEFINED_AREAS.map(area => (
                          <option key={area.slug} value={area.slug}>
                            {area.icon} {area.title}
                          </option>
                        ))}
                        <option value="custom">✨ Create Custom Area</option>
                      </select>
                    </div>

                    {/* Show custom slug/title inputs if custom selected */}
                    {useCustomSlug && (
                      <div className="grid grid-cols-2 gap-4 pt-3 border-t-2 border-[#E8934D]">
                        <div>
                          <label className="block text-xs text-[#6B4423] mb-2">Custom Slug *</label>
                          <input
                            type="text"
                            value={formData.slug}
                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                            placeholder="my-custom-area"
                            required
                          />
                          <p className="text-xs text-[#6B4423] mt-1">Use lowercase with hyphens</p>
                        </div>
                        <div>
                          <label className="block text-xs text-[#6B4423] mb-2">Custom Title *</label>
                          <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                            placeholder="My Custom Area"
                            required
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Show slug/title as readonly when editing */}
              {editingArea && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#6B4423] mb-2">Slug (URL)</label>
                    <input
                      type="text"
                      value={formData.slug}
                      className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-gray-200 text-[#2C1810]"
                      disabled
                    />
                    <p className="text-xs text-[#6B4423] mt-1">Cannot change slug when editing</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#6B4423] mb-2">Order *</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                      required
                    />
                    <p className="text-xs text-[#6B4423] mt-1">Display order on homepage</p>
                  </div>
                </div>
              )}

              {/* Order field for new areas */}
              {!editingArea && (
                <div>
                  <label className="block text-sm font-semibold text-[#6B4423] mb-2">Display Order *</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                    required
                  />
                  <p className="text-xs text-[#6B4423] mt-1">Order in which area appears on homepage (1, 2, 3...)</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  placeholder="Mental Health"
                  required
                  disabled={!editingArea && !useCustomSlug && formData.slug}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Subtitle</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  placeholder="Supporting Your Emotional Wellbeing"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={5}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  placeholder="Describe this area of focus..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Additional Details</label>
                <textarea
                  value={formData.additional_details}
                  onChange={(e) => setFormData({ ...formData, additional_details: e.target.value })}
                  rows={5}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  placeholder="Additional information that appears below images..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Image 1 Link (Optional)</label>
                <input
                  type="url"
                  value={formData.image_1_link}
                  onChange={(e) => setFormData({ ...formData, image_1_link: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  placeholder="https://example.com (Image 1 will link here when clicked)"
                />
                <p className="text-xs text-[#6B4423] mt-1">When visitors click on Image 1, they'll be taken to this URL</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-2">Image 2 Link (Optional)</label>
                <input
                  type="url"
                  value={formData.image_2_link}
                  onChange={(e) => setFormData({ ...formData, image_2_link: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  placeholder="https://example.com (Image 2 will link here when clicked)"
                />
                <p className="text-xs text-[#6B4423] mt-1">When visitors click on Image 2, they'll be taken to this URL</p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="w-5 h-5"
                />
                <label htmlFor="enabled" className="text-sm font-semibold text-[#6B4423]">
                  Show on website
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="retro-button flex-1 flex items-center justify-center gap-2">
                  <Save className="w-4 h-4" />
                  {editingArea ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="retro-button bg-[#6B4423] hover:bg-[#3D2817] flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
